import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatINR } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronRight, ChevronDown, FileSpreadsheet, Ship } from "lucide-react";
import { toast } from "sonner";

/**
 * Landed-Cost Reconciliation
 * Groups assets by invoice_number so accounts can reconcile total capitalised
 * value against BBS's books. Expandable rows drill down into per-machine detail.
 */
export default function LandedCostReconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    api.get("/reports/landed-cost-reconciliation")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load reconciliation"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (inv) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(inv)) next.delete(inv); else next.add(inv);
    return next;
  });

  const exportCsv = () => {
    if (!data?.groups?.length) return;
    const rows = [["Invoice #", "Machines", "Origin Currency", "Earliest Install", "Landed INR", "Current Book Value INR", "Depreciation INR"]];
    for (const g of data.groups) {
      rows.push([
        g.invoice_number, g.machine_count,
        (g.origin_currencies || []).join(", ") || "INR",
        g.earliest_install_date || "",
        g.total_landed_inr, g.total_current_value_inr, g.total_depreciation_inr,
      ]);
    }
    // append asset-level detail
    rows.push([]);
    rows.push(["--- Per-asset detail ---"]);
    rows.push(["Invoice #", "Asset ID", "Name", "Brand", "Model", "Serial", "Install Date", "Landed INR", "Current INR", "Depreciation INR"]);
    for (const g of data.groups) {
      for (const a of g.assets) {
        rows.push([g.invoice_number, a.asset_id, a.name, a.brand || "", a.model_number || "",
          a.serial_number || "", a.installation_date || "", a.purchase_value_inr, a.current_value_inr, a.accumulated_depreciation_inr]);
      }
    }
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `landed-cost-reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-slate-500 text-sm">Loading reconciliation...</div>;
  if (!data || !data.groups?.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-slate-500">
          <Ship className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          No assets tagged with an invoice number yet. Once you bulk-import capex invoices they'll show up here grouped for reconciliation.
        </CardContent>
      </Card>
    );
  }

  const { totals, groups } = data;

  return (
    <Card data-testid="landed-cost-report">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Ship className="w-4 h-4 text-[#7C1D3A]" /> Landed-Cost Reconciliation
          </CardTitle>
          <div className="text-xs text-slate-500 mt-1">
            {totals.invoices} invoices · {totals.machines} machines · <span className="font-mono">{formatINR(totals.landed_inr)}</span> landed · <span className="font-mono">{formatINR(totals.current_value_inr)}</span> current
          </div>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-1 text-xs text-[#7C1D3A] hover:text-[#5B0E28] font-semibold"
          data-testid="landed-cost-export"
        >
          <FileSpreadsheet className="w-4 h-4" /> Export CSV
        </button>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-t border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <th className="pl-4 pr-2 py-3 w-8"></th>
                <th className="px-4 py-3">Invoice #</th>
                <th className="px-4 py-3 text-right">Machines</th>
                <th className="px-4 py-3">Origin</th>
                <th className="px-4 py-3">Earliest Install</th>
                <th className="px-4 py-3 text-right">Landed INR</th>
                <th className="px-4 py-3 text-right">Current Value</th>
                <th className="px-4 py-3 text-right">Depreciation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {groups.map((g) => {
                const open = expanded.has(g.invoice_number);
                const isUnassigned = g.invoice_number === "— unassigned —";
                return (
                  <FragmentGroup
                    key={g.invoice_number}
                    g={g}
                    open={open}
                    isUnassigned={isUnassigned}
                    onToggle={() => toggle(g.invoice_number)}
                  />
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                <td colSpan={2} className="px-4 py-3 text-right">Total</td>
                <td className="px-4 py-3 text-right font-mono">{totals.machines}</td>
                <td colSpan={2}></td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(totals.landed_inr)}</td>
                <td className="px-4 py-3 text-right font-mono text-[#7C1D3A]">{formatINR(totals.current_value_inr)}</td>
                <td className="px-4 py-3 text-right font-mono text-red-600">{formatINR(totals.depreciation_inr)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function FragmentGroup({ g, open, isUnassigned, onToggle }) {
  return (
    <>
      <tr
        className={`cursor-pointer ${isUnassigned ? "bg-amber-50 hover:bg-amber-100/60" : "hover:bg-slate-50"}`}
        onClick={onToggle}
        data-testid={`invoice-group-${g.invoice_number}`}
      >
        <td className="pl-4 pr-2 py-3 w-8">
          {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        </td>
        <td className="px-4 py-3 font-mono text-xs font-semibold text-[#7C1D3A]">
          {g.invoice_number}
          {g.is_imported && !isUnassigned && (
            <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-sans font-semibold">IMPORT</span>
          )}
        </td>
        <td className="px-4 py-3 text-right font-mono">{g.machine_count}</td>
        <td className="px-4 py-3 text-xs text-slate-600">{(g.origin_currencies || []).join(", ") || "INR"}</td>
        <td className="px-4 py-3 text-xs text-slate-600 font-mono">{g.earliest_install_date || "—"}</td>
        <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{formatINR(g.total_landed_inr)}</td>
        <td className="px-4 py-3 text-right font-mono text-[#7C1D3A] font-semibold">{formatINR(g.total_current_value_inr)}</td>
        <td className="px-4 py-3 text-right font-mono text-red-600">{formatINR(g.total_depreciation_inr)}</td>
      </tr>
      {open && g.assets.map((a) => (
        <tr key={a.id} className="bg-slate-50/60 text-xs">
          <td className="w-8"></td>
          <td colSpan={2} className="pl-8 pr-2 py-2 text-slate-700">
            <div className="font-mono font-semibold text-[#7C1D3A]">{a.asset_id}</div>
            <div className="text-slate-900">{a.name}</div>
            <div className="text-slate-500">{a.brand} · {a.model_number} · SN {a.serial_number || "—"}</div>
          </td>
          <td className="px-4 py-2 text-slate-500 text-xs">{a.origin_currency || "INR"}</td>
          <td className="px-4 py-2 text-slate-500 font-mono">{a.installation_date || "—"}</td>
          <td className="px-4 py-2 text-right font-mono text-slate-800">{formatINR(a.purchase_value_inr)}</td>
          <td className="px-4 py-2 text-right font-mono text-[#7C1D3A]">{formatINR(a.current_value_inr)}</td>
          <td className="px-4 py-2 text-right font-mono text-red-500">{formatINR(a.accumulated_depreciation_inr)}</td>
        </tr>
      ))}
    </>
  );
}
